import React, { useRef, useEffect } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { OSM } from "ol/source";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { Style, Fill, Stroke } from "ol/style";
import { Circle as CircleStyle } from "ol/style";
import { fromLonLat } from "ol/proj";

const style = {
  width: "800px",
  height: "500px",
  borderRadius: "8px",
  margin: "1rem auto"
};


// layers: [{ geojson, style: { fill, stroke, strokeWidth } }]
function OpenLayersMap({ layers }) {
  const mapRef = useRef();
  const olMapRef = useRef();

  useEffect(() => {
    if (!layers || layers.length === 0) return;
    // Remove previous map instance
    if (olMapRef.current) {
      olMapRef.current.setTarget(null);
      olMapRef.current = null;
    }
    // Create vector layers from layers prop
    const vectorLayers = layers.map(layer => {
      const source = new VectorSource({
        features: new GeoJSON().readFeatures(layer.geojson, {
          featureProjection: "EPSG:3857"
        })
      });
      // Style function to handle both polygons/lines and points
      const styleFn = feature => {
        const geomType = feature.getGeometry().getType();
        if (geomType === 'Point' || geomType === 'MultiPoint') {
          return new Style({
            image: new CircleStyle({
              radius: 1, // Decreased from 7 to 4
              fill: new Fill({ color: layer.style?.fill || '#000000' , size: 1}),
              stroke: new Stroke({ color: layer.style?.stroke || '#800020', width: 1 })
            })
          });
        }
        return new Style({
          fill: new Fill({ color: layer.style?.fill || '#e0e0e0' }),
          stroke: new Stroke({ color: layer.style?.stroke || '#363032', width: layer.style?.strokeWidth || 2 })
        });
      };
      return new VectorLayer({
        source,
        style: styleFn
      });
    });
    // Create map
    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM({
            attributions: [] // Remove OSM attribution
          })
        }),
        ...vectorLayers
      ],
      view: new View({
        center: fromLonLat([2.2137, 47.0176]),
        zoom: 6,
        minZoom: 6,
        maxZoom: 12,
      }),
      controls: [], // Remove default controls (including zoom)
    });
    olMapRef.current = map;

    // Create popup overlay element
    let popupContainer = document.createElement('div');
    popupContainer.style.position = 'absolute';
    popupContainer.style.background = 'rgba(255,255,255,0.95)';
    popupContainer.style.border = '1px solid #800020';
    popupContainer.style.borderRadius = '8px';
    popupContainer.style.padding = '12px 16px';
    popupContainer.style.minWidth = '180px';
    popupContainer.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    popupContainer.style.pointerEvents = 'auto';
    popupContainer.style.zIndex = 1000;
    popupContainer.style.display = 'none';
    popupContainer.style.fontFamily = 'Arial, sans-serif';
    popupContainer.style.fontSize = '1em';
    popupContainer.style.color = '#222';
    popupContainer.style.maxHeight = '300px';
    popupContainer.style.overflowY = 'auto';
    popupContainer.id = 'ol-popup';
    mapRef.current.appendChild(popupContainer);

    // Remove popup on map click elsewhere
    map.on('click', function () {
      popupContainer.style.display = 'none';
    });

    // Add click handler for point features
    map.on('singleclick', function (evt) {
      let found = false;
      map.forEachFeatureAtPixel(evt.pixel, function (feature) {
        const geomType = feature.getGeometry().getType();
        if (geomType === 'Point' || geomType === 'MultiPoint') {
          found = true;
          const props = feature.getProperties();
          const { geometry, ...rest } = props;
          popupContainer.innerHTML =
            '<b>Festival Info</b><hr style="margin:6px 0">' +
            Object.entries(rest)
              .map(([k, v]) => `<div style='margin-bottom:2px'><b>${k}:</b> ${v}</div>`)
              .join('');
          popupContainer.style.left = (evt.pixel[0] + 20) + 'px';
          popupContainer.style.top = (evt.pixel[1] - 10) + 'px';
          popupContainer.style.display = 'block';
        }
      });
      if (!found) popupContainer.style.display = 'none';
    });

    // Cleanup
    return () => {
      if (olMapRef.current) {
        olMapRef.current.setTarget(null);
        olMapRef.current = null;
      }
    };
  }, [layers]);

  return <div ref={mapRef} style={{ width: "800px", height: "610px", borderRadius: "8px", margin: "1rem auto" }}></div>;
}

export default OpenLayersMap;
